import React, { useState, useEffect, useMemo, useRef } from 'react';
import { gql } from '@apollo/client';
import styled from 'styled-components';
import { Button, CardContainer, StatusBadge, Input, DocumentPreview, useGraphQLMutation } from '@tviklax/ui-components';
import { useRole } from '../../../context/RoleContext';
import { useUser } from '../../../context/UserContext';
import { API_BASE_URL } from '../../../shared/constants/env';

type ComplianceStatus = 'draft' | 'submitted' | 'docs-requested' | 'docs-resubmitted' | 'compliance-approved' | 'remittance-initiated' | 'remittance-completed' | 'locked';

type ChecklistStatus = 'pending' | 'uploaded' | 'approved' | 'rejected';

type DocumentOwner = 'supplier' | 'finance' | 'ops' | 'agent';

interface ChecklistItem {
  requirement: string;
  status: ChecklistStatus;
  file?: string;
  notes?: string;
  required: boolean;
  expiryDate?: string;
  owner: DocumentOwner;
  requestedOn?: string;
  uploadedOn?: string;
  approvedOn?: string;
}

interface RemittanceTranche {
  percentage: number;
  amount: number;
  date: string;
  milestone: string;
}

interface ForexComplianceData {
  id?: string;
  orderId: string;
  buyer: string;
  supplier: string;
  country: string;
  currency: string;
  totalValue: number;
  incoterms: string;
  expectedShipmentDate: string;
  complianceStatus: ComplianceStatus;
  checklist: ChecklistItem[];
  remittancePlan: {
    totalAmount: number;
    tranches: RemittanceTranche[];
  };
  bankMetadata: {
    adBankName: string;
    adBankCode: string;
    rbiApprovalNumber?: string;
    femaReference?: string;
    bankSubmissionRef?: string;
    declarationDate?: string;
  };
  approvalHistory: Array<{
    timestamp: string;
    action: string;
    by: string;
    reason?: string;
    newStatus?: ComplianceStatus;
  }>;
  isLocked: boolean;
}

interface ForexComplianceDetailProps {
  selectedOrderId?: string;
  onBack?: () => void;
  initialData?: ForexComplianceData;
  onRecordUpdated?: () => Promise<unknown> | void;
  orders?: Array<{
    id: string;
    buyer: string;
    supplier: string;
    country: string;
    currency: string;
    totalValue: number;
    complianceStatus: ComplianceStatus;
    expectedShipmentDate: string;
  }>;
}

const FOREX_RECORD_FRAGMENT = `
  success
  message
  record {
    id
    orderId: id
    buyer
    supplier
    country
    currency
    totalValue
    incoterms
    expectedShipmentDate
    complianceStatus
    isLocked
    checklist {
      requirement
      status
      file
      notes
      required
      expiryDate
      owner
      requestedOn
      uploadedOn
      approvedOn
    }
    remittancePlan {
      totalAmount
      tranches {
        percentage
        amount
        date
        milestone
      }
    }
    bankMetadata {
      adBankName
      adBankCode
      rbiApprovalNumber
      femaReference
      bankSubmissionRef
      declarationDate
    }
    approvalHistory {
      timestamp
      action
      by
      reason
      newStatus
    }
  }
`;

const UPLOAD_FOREX_DOCUMENT_MUTATION = gql`
  mutation UploadForexComplianceDocument($input: UploadForexComplianceDocumentInput!) {
    uploadForexComplianceDocument(input: $input) {
      ${FOREX_RECORD_FRAGMENT}
    }
  }
`;

const REVIEW_FOREX_DOCUMENT_MUTATION = gql`
  mutation ReviewForexComplianceDocument($input: ReviewForexComplianceDocumentInput!) {
    reviewForexComplianceDocument(input: $input) {
      ${FOREX_RECORD_FRAGMENT}
    }
  }
`;

const ADVANCE_FOREX_COMPLIANCE_MUTATION = gql`
  mutation AdvanceForexCompliance($input: AdvanceForexComplianceInput!) {
    advanceForexCompliance(input: $input) {
      ${FOREX_RECORD_FRAGMENT}
    }
  }
`;

const UPSERT_FOREX_BANK_METADATA_MUTATION = gql`
  mutation UpsertForexBankMetadata($input: UpsertForexComplianceBankMetadataInput!) {
    upsertForexBankMetadata(input: $input) {
      ${FOREX_RECORD_FRAGMENT}
    }
  }
`;

interface ForexMutationPayload {
  success: boolean;
  message: string;
  record?: ForexComplianceData;
}

interface UploadForexDocumentMutationData {
  uploadForexComplianceDocument: ForexMutationPayload;
}

interface ReviewForexDocumentMutationData {
  reviewForexComplianceDocument: ForexMutationPayload;
}

interface AdvanceForexComplianceMutationData {
  advanceForexCompliance: ForexMutationPayload;
}

interface UpsertForexBankMetadataMutationData {
  upsertForexBankMetadata: ForexMutationPayload;
}

interface DocumentValidationResult {
  is_valid_format: boolean;
  validation_score: number;
  checks: Array<{ name: string; passed: boolean; details: string }>;
  missing_required_fields: string[];
  warnings: string[];
  extracted_data: Record<string, unknown>;
  finalized_hs_code?: string | null;
  hs_code_candidates?: string[];
  needs_manual_review?: boolean;
  manual_review_reasons?: string[];
}

const formatDisplayDate = (value?: string): string => {
  if (!value) {
    return '—';
  }

  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? value : parsed.toLocaleDateString('en-IN');
};

const normalizeComplianceRecord = (
  record?: Partial<ForexComplianceData> & { id?: string }
): ForexComplianceData | undefined => {
  if (!record) {
    return undefined;
  }

  const orderId = record.orderId || record.id;
  if (!orderId) {
    return undefined;
  }

  return {
    ...record,
    id: record.id || orderId,
    orderId,
  } as ForexComplianceData;
};

const Page = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[6]};
  padding: ${({ theme }) => `${theme.spacing[5]} ${theme.spacing[6]}`};
  max-width: 1400px;
  margin: 0 auto;

  @media (max-width: 768px) {
    padding: ${({ theme }) => `${theme.spacing[4]} ${theme.spacing[4]}`};
    gap: ${({ theme }) => theme.spacing[4]};
  }
`;

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: ${({ theme}) => theme.spacing[4]};
  gap: ${({ theme }) => theme.spacing[3]};

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const HeaderContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[1]};
`;

const Title = styled.h1`
  font-size: ${({ theme }) => theme.typography.fontSize['2xl']};
  font-weight: ${({ theme }) => theme.typography.fontWeight.bold};
  color: ${({ theme }) => theme.colors.gray900};
  margin: 0;
`;

const Subtitle = styled.p`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.gray600};
  margin: 0;
`;

const SectionTitle = styled.h3`
  font-size: ${({ theme }) => theme.typography.fontSize.lg};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.gray900};
  margin: 0 0 ${({ theme }) => theme.spacing[3]} 0;
`;

const MetaGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: ${({ theme }) => theme.spacing[4]};

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const MetaItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[1]};
`;

const MetaLabel = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.gray600};
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const MetaValue = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  color: ${({ theme }) => theme.colors.gray900};
`;

const MetadataSyncStatus = styled.div<{ $tone: 'neutral' | 'success' | 'error' }>`
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  color: ${({ $tone, theme }) => {
    if ($tone === 'success') {
      return theme.colors.success;
    }
    if ($tone === 'error') {
      return theme.colors.error;
    }
    return theme.colors.gray600;
  }};
  margin-top: ${({ theme }) => theme.spacing[2]};
`;

const ChecklistTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: ${({ theme }) => theme.typography.fontSize.sm};

  th {
    text-align: left;
    padding: ${({ theme }) => theme.spacing[2]};
    background: ${({ theme }) => theme.colors.gray0};
    font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
    color: ${({ theme }) => theme.colors.gray700};
    border-bottom: 1px solid ${({ theme }) => theme.colors.gray200};
  }

  td {
    padding: ${({ theme }) => theme.spacing[3]};
    border-bottom: 1px solid ${({ theme }) => theme.colors.gray100};
  }

  tr:last-child td {
    border-bottom: none;
  }

  @media (max-width: 768px) {
    display: block;

    thead {
      display: none;
    }

    tbody, tr, td {
      display: block;
    }

    tr {
      margin-bottom: ${({ theme }) => theme.spacing[4]};
      border: 1px solid ${({ theme }) => theme.colors.gray200};
      border-radius: ${({ theme }) => theme.borderRadius.md};
      padding: ${({ theme }) => theme.spacing[3]};
    }

    td {
      padding: ${({ theme }) => theme.spacing[2]} 0;
      border: none;

      &:before {
        content: attr(data-label);
        font-weight: 600;
        color: ${({ theme }) => theme.colors.gray600};
        display: block;
        margin-bottom: 4px;
        font-size: ${({ theme }) => theme.typography.fontSize.xs};
        text-transform: uppercase;
      }
    }
  }
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[2]};
`;

const Label = styled.label`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  color: ${({ theme }) => theme.colors.gray700};
`;

const FormActions = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing[3]};
  margin-top: ${({ theme }) => theme.spacing[4]};

  @media (max-width: 640px) {
    flex-direction: column;
  }
`;

const AlertBox = styled.div<{ $type: 'info' | 'warning' | 'success' | 'error' }>`
  padding: ${({ theme }) => theme.spacing[3]};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  margin-bottom: ${({ theme }) => theme.spacing[3]};

  ${({ $type }) => {
    switch ($type) {
      case 'info':
        return `background: #eff6ff; border: 1px solid #3b82f6; color: #1e40af;`;
      case 'warning':
        return `background: #fffbeb; border: 1px solid #f59e0b; color: #92400e;`;
      case 'success':
        return `background: #f0fdf4; border: 1px solid #10b981; color: #065f46;`;
      case 'error':
        return `background: #fef2f2; border: 1px solid #ef4444; color: #991b1b;`;
    }
  }}
`;

const StyledCheckbox = styled.input.attrs({ type: 'checkbox' })`
  width: 16px;
  height: 16px;
  cursor: pointer;
  accent-color: ${({ theme }) => theme.colors.primary};
  
  &:checked {
    background-color: ${({ theme }) => theme.colors.primary};
    border-color: ${({ theme }) => theme.colors.primary};
  }
`;

const getStatusLabel = (status: ComplianceStatus): string => {
  const labels: Record<ComplianceStatus, string> = {
    'draft': '⚪ Draft',
    'submitted': '🔵 Submitted',
    'docs-requested': '🟡 Docs Requested',
    'docs-resubmitted': '🟠 Docs Resubmitted',
    'compliance-approved': '🟢 Compliance Approved',
    'remittance-initiated': '🔷 Remittance Initiated',
    'remittance-completed': '✅ Remittance Completed',
    'locked': '🔒 Locked',
  };
  return labels[status];
};

const getStatusVariant = (status: ComplianceStatus): 'neutral' | 'success' | 'warning' | 'error' | 'info' | 'locked' => {
  const variants: Record<ComplianceStatus, 'neutral' | 'success' | 'warning' | 'error' | 'info' | 'locked'> = {
    'draft': 'neutral',
    'submitted': 'info',
    'docs-requested': 'warning',
    'docs-resubmitted': 'warning',
    'compliance-approved': 'success',
    'remittance-initiated': 'success',
    'remittance-completed': 'success',
    'locked': 'locked',
  };
  return variants[status];
};

const getChecklistStatusIcon = (status: ChecklistStatus): string => {
  switch (status) {
    case 'approved': return '✅';
    case 'uploaded': return '📄';
    case 'rejected': return '❌';
    case 'pending': return '⏳';
  }
};

const getOwnerLabel = (owner: DocumentOwner): string => {
  const labels: Record<DocumentOwner, string> = {
    'supplier': 'Supplier',
    'finance': 'Finance',
    'ops': 'Operations',
    'agent': 'Agent',
  };
  return labels[owner];
};

const canUploadChecklistItem = (
  role: string,
  owner: DocumentOwner,
  status: ChecklistStatus,
  isLocked: boolean
): boolean => {
  if (isLocked || status !== 'pending') {
    return false;
  }

  if (role === 'admin') {
    return true;
  }

  return (
    (owner === 'supplier' && role === 'supplier') ||
    (owner === 'finance' && role === 'finance') ||
    (owner === 'ops' && role === 'ops') ||
    (owner === 'agent' && role === 'agent')
  );
};

const canReviewChecklistItem = (
  role: string,
  status: ChecklistStatus,
  isLocked: boolean,
  requirement: string
): boolean => {
  if (isLocked || status !== 'uploaded') {
    return false;
  }

  if (role === 'finance' || role === 'admin') {
    return true;
  }

  // Operations can review HS declarations directly.
  return role === 'ops' && requirement.trim().toLowerCase() === 'hs code declaration';
};

const getUploadActorLabel = (role: string): string => {
  if (role === 'supplier') return 'Supplier';
  if (role === 'finance') return 'Finance';
  if (role === 'ops') return 'Operations';
  if (role === 'admin') return 'Admin';
  if (role === 'agent') return 'Agent';
  return 'User';
};

const getAllowedFileExtensions = (requirement: string): string[] => {
  const map: Record<string, string[]> = {
    'proforma invoice': ['pdf'],
    'purchase order': ['pdf'],
    'hs code declaration': ['pdf', 'csv', 'xls', 'xlsx'],
    'supplier kyc': ['pdf', 'jpg', 'jpeg', 'png'],
    'packing list': ['pdf', 'xls', 'xlsx'],
    'remittance schedule': ['pdf', 'xls', 'xlsx', 'csv'],
    'fema declaration': ['pdf'],
  };
  return map[requirement.trim().toLowerCase()] || ['pdf'];
};

const getApiOrigin = (): string => API_BASE_URL.replace(/\/graphql\/?$/, '');

const buildGeneratedDocumentUrl = (relativePath: string): string => {
  const cleanRelative = relativePath
    .replace(/^\/+/, '')
    .split('/')
    .filter(Boolean)
    .map((segment) => encodeURIComponent(segment))
    .join('/');
  return `${getApiOrigin()}/generated-documents/${cleanRelative}`;
};

const uploadChecklistDocument = async (file: File): Promise<string> => {
  const formData = new FormData();
  formData.append('file', file);

  const response = await fetch(`${getApiOrigin()}/api/v1/forex/upload-document`, {
    method: 'POST',
    body: formData,
  });

  if (!response.ok) {
    let message = 'Document upload failed';
    try {
      const data = await response.json();
      message = data.detail || data.message || message;
    } catch {
      // Ignore JSON parsing errors and keep default message.
    }
    throw new Error(message);
  }

  const payload = await response.json();
  const fileUrl = payload.fileUrl || payload.file_url;
  if (!fileUrl || typeof fileUrl !== 'string') {
    throw new Error('Document upload did not return a valid file URL');
  }

  return fileUrl;
};

const resolveDocumentUrl = (filePath?: string): string | null => {
  if (!filePath) {
    return null;
  }

  const normalizedPath = filePath.trim();
  if (!normalizedPath) {
    return null;
  }

  if (/^https?:\/\//i.test(normalizedPath)) {
    return normalizedPath;
  }

  const apiOrigin = getApiOrigin();

  if (normalizedPath.startsWith('/generated-documents/')) {
    return `${apiOrigin}${normalizedPath}`;
  }

  if (normalizedPath.startsWith('generated_documents/')) {
    return buildGeneratedDocumentUrl(normalizedPath.replace(/^generated_documents\//, ''));
  }

  const unixMarker = '/generated_documents/';
  const windowsMarker = '\\generated_documents\\';
  const unixIndex = normalizedPath.indexOf(unixMarker);
  const windowsIndex = normalizedPath.toLowerCase().indexOf(windowsMarker.toLowerCase());

  if (unixIndex >= 0) {
    const relativePart = normalizedPath.substring(unixIndex + unixMarker.length);
    return buildGeneratedDocumentUrl(relativePart);
  }

  if (windowsIndex >= 0) {
    const relativePart = normalizedPath
      .substring(windowsIndex + windowsMarker.length)
      .replace(/\\/g, '/');
    return buildGeneratedDocumentUrl(relativePart);
  }

  // Backward compatibility: older checklist uploads may store only a bare filename.
  if (!normalizedPath.includes('/') && !normalizedPath.includes('\\') && normalizedPath.includes('.')) {
    const lowerName = normalizedPath.toLowerCase();
    if (lowerName.startsWith('po-')) {
      return buildGeneratedDocumentUrl(`purchase_orders/${normalizedPath}`);
    }
    return buildGeneratedDocumentUrl(`forex_uploads/${normalizedPath}`);
  }

  return normalizedPath;
};

const getDocumentDisplayName = (filePath?: string, requirement?: string): string => {
  if (filePath) {
    const normalized = filePath.split('?')[0].split('#')[0];
    const name = normalized.split('/').pop()?.split('\\').pop();
    if (name) {
      try {
        return decodeURIComponent(name);
      } catch {
        return name;
      }
    }
  }
  return requirement ? `${requirement} document` : 'Document';
};

const isPdfFile = (pathOrUrl?: string | null): boolean => {
  if (!pathOrUrl) {
    return false;
  }
  const cleanValue = pathOrUrl.split('?')[0].split('#')[0].trim().toLowerCase();
  return cleanValue.endsWith('.pdf');
};

const isAutoApprovedItem = (item: ChecklistItem): boolean => {
  const notes = (item.notes || '').toLowerCase();
  const hasAutoNote = notes.includes('auto-approved') || notes.includes('auto-uploaded from create order') || notes.includes('auto approved');
  return hasAutoNote && (item.status === 'approved' || item.status === 'uploaded');
};

export const ForexComplianceDetail: React.FC<ForexComplianceDetailProps> = ({
  selectedOrderId = 'ORD-2026-001',
  onBack,
  initialData,
  onRecordUpdated,
  orders = [],
}) => {
  const { role } = useRole();
  const { user } = useUser();
  const isFinance = role === 'finance' || role === 'admin';

  const currentOrder = orders.find(o => o.id === selectedOrderId) || orders[0];
  const normalizedInitialData = useMemo(() => normalizeComplianceRecord(initialData), [initialData]);

  const [complianceData, setComplianceData] = useState<ForexComplianceData>({
    id: normalizedInitialData?.id || normalizedInitialData?.orderId || currentOrder?.id || '',
    orderId: normalizedInitialData?.orderId || currentOrder?.id || '',
    buyer: normalizedInitialData?.buyer || currentOrder?.buyer || '',
    supplier: normalizedInitialData?.supplier || currentOrder?.supplier || '',
    country: normalizedInitialData?.country || currentOrder?.country || '',
    currency: normalizedInitialData?.currency || currentOrder?.currency || '',
    totalValue: normalizedInitialData?.totalValue ?? currentOrder?.totalValue ?? 0,
    incoterms: normalizedInitialData?.incoterms || '',
    expectedShipmentDate: normalizedInitialData?.expectedShipmentDate || currentOrder?.expectedShipmentDate || '',
    complianceStatus: normalizedInitialData?.complianceStatus || currentOrder?.complianceStatus || 'draft',
    checklist: normalizedInitialData?.checklist || [],
    remittancePlan: normalizedInitialData?.remittancePlan || {
      totalAmount: normalizedInitialData?.totalValue ?? currentOrder?.totalValue ?? 0,
      tranches: [],
    },
    bankMetadata: normalizedInitialData?.bankMetadata || {
      adBankName: '',
      adBankCode: '',
      rbiApprovalNumber: '',
      femaReference: '',
      bankSubmissionRef: '',
      declarationDate: '',
    },
    approvalHistory: normalizedInitialData?.approvalHistory || [],
    isLocked: normalizedInitialData?.isLocked || false,
  });

  const [showApprovalForm, setShowApprovalForm] = useState(false);
  const [approvalAction, setApprovalAction] = useState<'submit' | 'approve' | 'request-docs' | 'complete-remittance' | 'reject-item'>('approve');
  const [approvalReason, setApprovalReason] = useState('');
  const [uploadingFile, setUploadingFile] = useState<number | null>(null);
  const [uploadNote, setUploadNote] = useState('');
  const [itemActionIndex, setItemActionIndex] = useState<number | null>(null);
  const [itemActionType, setItemActionType] = useState<'approve' | 'reject' | null>(null);
  const [itemActionReason, setItemActionReason] = useState('');
  const [remittanceAmount, setRemittanceAmount] = useState('');
  const [remittancePercent, setRemittancePercent] = useState('');
  const [selectedDocRequests, setSelectedDocRequests] = useState<number[]>([]);
  const [docRequestComments, setDocRequestComments] = useState<Record<number, string>>({});
  const [globalRequestNote, setGlobalRequestNote] = useState('');
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [pdfPreviewUrl, setPdfPreviewUrl] = useState<string | null>(null);
  const [pdfPreviewLabel, setPdfPreviewLabel] = useState<string>('Document');
  const [validationResults, setValidationResults] = useState<Record<number, DocumentValidationResult | null>>({});
  const [bankMetadataSyncStatus, setBankMetadataSyncStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const lastAutoSyncedBankMetadataRef = useRef<string>('');
  const [uploadForexDocument] = useGraphQLMutation<UploadForexDocumentMutationData>(UPLOAD_FOREX_DOCUMENT_MUTATION, {
    showErrorToast: false,
    showSuccessToast: false,
  });
  const [reviewForexDocument] = useGraphQLMutation<ReviewForexDocumentMutationData>(REVIEW_FOREX_DOCUMENT_MUTATION, {
    showErrorToast: false,
    showSuccessToast: false,
  });
  const [advanceForexCompliance] = useGraphQLMutation<AdvanceForexComplianceMutationData>(ADVANCE_FOREX_COMPLIANCE_MUTATION, {
    showErrorToast: false,
    showSuccessToast: false,
  });
  const [upsertForexBankMetadata] = useGraphQLMutation<UpsertForexBankMetadataMutationData>(UPSERT_FOREX_BANK_METADATA_MUTATION, {
    showErrorToast: false,
    showSuccessToast: false,
  });

  const showTransientToast = (message: string) => {
    setToastMessage(message);
    setShowToast(true);
    setTimeout(() => setShowToast(false), 4000);
  };

  const syncMutationRecord = async (
    payload?: ForexMutationPayload,
    fallbackMessage?: string,
    options?: { suppressToast?: boolean },
  ) => {
    const normalizedRecord = normalizeComplianceRecord(payload?.record);
    if (!payload?.success || !normalizedRecord) {
      throw new Error(payload?.message || fallbackMessage || 'Forex compliance update failed');
    }
    setComplianceData(normalizedRecord);
    if (onRecordUpdated) {
      await onRecordUpdated();
    }
    if (!options?.suppressToast) {
      showTransientToast(payload.message);
    }
  };

  // State machine: valid transitions
  const canTransitionTo = (newStatus: ComplianceStatus): boolean => {
    const transitions: Record<ComplianceStatus, ComplianceStatus[]> = {
      'draft': ['submitted'],
      'submitted': ['docs-requested', 'compliance-approved'],
      'docs-requested': ['docs-resubmitted'],
      'docs-resubmitted': ['docs-requested', 'compliance-approved'],
      'compliance-approved': ['remittance-initiated'],
      'remittance-initiated': ['remittance-completed'],
      'remittance-completed': ['locked'],
      'locked': [],
    };
    return transitions[complianceData.complianceStatus]?.includes(newStatus) || false;
  };

  // Update compliance data when order changes
  useEffect(() => {
    if (normalizedInitialData) {
      setComplianceData(normalizedInitialData);
      return;
    }

    if (currentOrder) {
      setComplianceData(prev => ({
        ...prev,
        id: currentOrder.id,
        orderId: currentOrder.id,
        buyer: currentOrder.buyer,
        supplier: currentOrder.supplier,
        country: currentOrder.country,
        currency: currentOrder.currency,
        totalValue: currentOrder.totalValue,
        expectedShipmentDate: currentOrder.expectedShipmentDate,
        complianceStatus: currentOrder.complianceStatus,
      }));
    }
  }, [selectedOrderId, currentOrder, normalizedInitialData]);

  useEffect(() => {
    setPdfPreviewUrl(null);
    setPdfPreviewLabel('Document');
  }, [complianceData.orderId]);

  const normalizedBankMetadata = useMemo(() => ({
    adBankName: complianceData.bankMetadata.adBankName?.trim() || '',
    adBankCode: complianceData.bankMetadata.adBankCode?.trim() || '',
    rbiApprovalNumber: complianceData.bankMetadata.rbiApprovalNumber?.trim() || '',
    femaReference: complianceData.bankMetadata.femaReference?.trim() || '',
    bankSubmissionRef: complianceData.bankMetadata.bankSubmissionRef?.trim() || '',
    declarationDate: complianceData.bankMetadata.declarationDate?.trim() || '',
  }), [
    complianceData.bankMetadata.adBankCode,
    complianceData.bankMetadata.adBankName,
    complianceData.bankMetadata.bankSubmissionRef,
    complianceData.bankMetadata.declarationDate,
    complianceData.bankMetadata.femaReference,
    complianceData.bankMetadata.rbiApprovalNumber,
  ]);

  const bankMetadataFingerprint = useMemo(
    () => JSON.stringify(normalizedBankMetadata),
    [normalizedBankMetadata],
  );

  useEffect(() => {
    if (normalizedInitialData) {
      const fingerprint = JSON.stringify({
        adBankName: normalizedInitialData.bankMetadata.adBankName?.trim() || '',
        adBankCode: normalizedInitialData.bankMetadata.adBankCode?.trim() || '',
        rbiApprovalNumber: normalizedInitialData.bankMetadata.rbiApprovalNumber?.trim() || '',
        femaReference: normalizedInitialData.bankMetadata.femaReference?.trim() || '',
        bankSubmissionRef: normalizedInitialData.bankMetadata.bankSubmissionRef?.trim() || '',
        declarationDate: normalizedInitialData.bankMetadata.declarationDate?.trim() || '',
      });
      lastAutoSyncedBankMetadataRef.current = fingerprint;
    }
  }, [normalizedInitialData]);

  useEffect(() => {
    if (!isFinance || complianceData.isLocked || !complianceData.orderId) {
      return undefined;
    }

    const hasMetadata = Object.values(normalizedBankMetadata).some(Boolean);
    if (!hasMetadata) {
      return undefined;
    }

    if (bankMetadataFingerprint === lastAutoSyncedBankMetadataRef.current) {
      return undefined;
    }

    const timeoutId = window.setTimeout(async () => {
      try {
        setBankMetadataSyncStatus('saving');
        const response = await upsertForexBankMetadata({
          variables: {
            input: {
              recordId: complianceData.orderId,
              adBankName: normalizedBankMetadata.adBankName || null,
              adBankCode: normalizedBankMetadata.adBankCode || null,
              rbiApprovalNumber: normalizedBankMetadata.rbiApprovalNumber || null,
              femaReference: normalizedBankMetadata.femaReference || null,
              bankSubmissionRef: normalizedBankMetadata.bankSubmissionRef || null,
              declarationDate: normalizedBankMetadata.declarationDate || null,
              autoGenerateDocument: true,
              actor: 'Finance Automation',
            },
          },
        });

        await syncMutationRecord(
          response.data?.upsertForexBankMetadata,
          'Unable to save bank metadata',
          { suppressToast: true },
        );
        lastAutoSyncedBankMetadataRef.current = bankMetadataFingerprint;
        setBankMetadataSyncStatus('saved');
      } catch (error) {
        setBankMetadataSyncStatus('error');
        const message = error instanceof Error ? error.message : 'Unable to save bank metadata';
        showTransientToast(message);
      }
    }, 900);

    return () => window.clearTimeout(timeoutId);
  }, [
    bankMetadataFingerprint,
    complianceData.isLocked,
    complianceData.orderId,
    isFinance,
    normalizedBankMetadata,
    upsertForexBankMetadata,
  ]);

  const openPdfPreview = (url: string, label: string) => {
    setPdfPreviewUrl(url);
    setPdfPreviewLabel(label);
  };

  const handleFileUpload = async (itemIndex: number, file: File, note?: string) => {
    setUploadingFile(itemIndex);
    try {
      const requirement = complianceData.checklist[itemIndex]?.requirement;
      const actor = getUploadActorLabel(role);
      const uploadedFileUrl = await uploadChecklistDocument(file);
      const response = await uploadForexDocument({
        variables: {
          input: {
            recordId: complianceData.orderId,
            requirement,
            fileName: uploadedFileUrl,
            note,
            actor,
          },
        },
      });
      await syncMutationRecord(response.data?.uploadForexComplianceDocument, 'Document upload failed');
      setUploadNote('');

      const normalizedRequirement = requirement?.toLowerCase().trim();
      let validationEndpoint: string | null = null;

      // Auto-validate proforma invoices and HS code declarations after upload.
      if (normalizedRequirement === 'proforma invoice' && file.name.toLowerCase().endsWith('.pdf')) {
        validationEndpoint = '/api/v1/forex/validate-proforma-invoice/upload';
      } else if (
        normalizedRequirement === 'hs code declaration' &&
        ['.pdf', '.csv', '.xls', '.xlsx'].some((ext) => file.name.toLowerCase().endsWith(ext))
      ) {
        validationEndpoint = '/api/v1/forex/validate-hs-code-declaration/upload';
      }

      if (validationEndpoint) {
        try {
          const formData = new FormData();
          formData.append('file', file);
          const res = await fetch(`${getApiOrigin()}${validationEndpoint}`, {
            method: 'POST',
            body: formData,
          });
          if (res.ok) {
            const result = (await res.json()) as DocumentValidationResult;
            setValidationResults(prev => ({ ...prev, [itemIndex]: result }));
            if (!result.is_valid_format) {
              showTransientToast(`⚠️ Document uploaded but has format issues (score: ${result.validation_score.toFixed(0)}%). Please review.`);
            } else if (normalizedRequirement === 'hs code declaration') {
              showTransientToast(`✅ HS verification passed (score: ${result.validation_score.toFixed(0)}%).`);
            }
          } else {
            let message = 'verification failed';
            try {
              const payload = await res.json();
              message = payload.detail || payload.message || message;
            } catch {
              // Keep fallback message.
            }
            showTransientToast(`⚠️ Document uploaded, but verification did not complete: ${message}`);
          }
        } catch {
          // Validation failure is non-blocking; upload already succeeded
          showTransientToast('⚠️ Document uploaded, but verification service is unreachable right now.');
        }
      }
    } catch (error) {
      showTransientToast(`❌ ${(error as Error).message}`);
    } finally {
      setUploadingFile(null);
    }
  };

  const handleItemAction = async (itemIndex: number, action: 'approve' | 'reject', reason?: string) => {
    try {
      const requirement = complianceData.checklist[itemIndex]?.requirement;
      const actor = user?.name || (role === 'ops' ? 'Operations' : role === 'admin' ? 'Admin' : 'Finance');
      const response = await reviewForexDocument({
        variables: {
          input: {
            recordId: complianceData.orderId,
            requirement,
            action,
            reason,
            actor,
          },
        },
      });
      await syncMutationRecord(response.data?.reviewForexComplianceDocument, 'Document review failed');
      setItemActionIndex(null);
      setItemActionType(null);
      setItemActionReason('');
    } catch (error) {
      showTransientToast(`❌ ${(error as Error).message}`);
    }
  };

  const handleApproval = async () => {
    // Determine next state based on current state and action
    let newStatus: ComplianceStatus | null = null;

    if (approvalAction === 'submit' && complianceData.complianceStatus === 'draft') {
      newStatus = 'submitted';
    } else if (approvalAction === 'approve') {
      if (complianceData.complianceStatus === 'submitted' || complianceData.complianceStatus === 'docs-resubmitted') {
        newStatus = 'compliance-approved';
      } else if (complianceData.complianceStatus === 'compliance-approved') {
        newStatus = 'remittance-initiated';
      }
    } else if (approvalAction === 'request-docs') {
      if (complianceData.complianceStatus === 'submitted') {
        newStatus = 'docs-requested';
      } else if (complianceData.complianceStatus === 'docs-resubmitted') {
        newStatus = 'docs-requested';
      } else if (complianceData.complianceStatus === 'docs-requested') {
        // Stay in docs-requested status when sending reminder
        newStatus = 'docs-requested';
      }
    } else if (approvalAction === 'complete-remittance' && complianceData.complianceStatus === 'remittance-initiated') {
      newStatus = 'remittance-completed';
    }

    // Validate state transition (allow same-state transitions for reminders)
    if (!newStatus || (!canTransitionTo(newStatus) && newStatus !== complianceData.complianceStatus)) {
      alert('Invalid state transition');
      return;
    }

    // Validate required fields based on action
    if (approvalAction === 'request-docs') {
      if (selectedDocRequests.length === 0) {
        alert('Please select at least one document to request');
        return;
      }
    }

    if (
      (approvalAction === 'approve' && complianceData.complianceStatus === 'compliance-approved') ||
      approvalAction === 'complete-remittance'
    ) {
      if (approvalAction === 'approve' && complianceData.complianceStatus === 'compliance-approved') {
        const hasAmount = Boolean(remittanceAmount.trim());
        const hasPercent = Boolean(remittancePercent.trim());
        if (!hasAmount && !hasPercent) {
          alert('Please provide either remittance amount or remittance percent.');
          return;
        }
      }

      if (approvalAction === 'complete-remittance' && !approvalReason.trim()) {
        alert('Please provide remittance completion details before continuing.');
        return;
      }
    }

    // Update compliance data
    let mutationAction: string = approvalAction;
    if (approvalAction === 'approve' && newStatus === 'remittance-initiated') {
      mutationAction = 'initiate-remittance';
    }

    const parsedRemittanceAmount = remittanceAmount.trim() ? Number(remittanceAmount) : undefined;
    const parsedRemittancePercent = remittancePercent.trim() ? Number(remittancePercent) : undefined;
    if (parsedRemittanceAmount !== undefined && (Number.isNaN(parsedRemittanceAmount) || parsedRemittanceAmount <= 0)) {
      alert('Remittance amount must be a positive number.');
      return;
    }
    if (parsedRemittancePercent !== undefined && (Number.isNaN(parsedRemittancePercent) || parsedRemittancePercent <= 0 || parsedRemittancePercent > 100)) {
      alert('Remittance percent must be between 0 and 100.');
      return;
    }

    try {
      const response = await advanceForexCompliance({
        variables: {
          input: {
            recordId: complianceData.orderId,
            action: mutationAction,
            reason: approvalAction === 'request-docs' ? globalRequestNote || approvalReason : approvalReason,
            actor: user?.name || 'Finance',
            remittanceAmount: mutationAction === 'initiate-remittance' ? parsedRemittanceAmount : undefined,
            remittancePercent: mutationAction === 'initiate-remittance' ? parsedRemittancePercent : undefined,
            requestedDocuments: approvalAction === 'request-docs'
              ? selectedDocRequests.map((index) => complianceData.checklist[index].requirement)
              : undefined,
            requestComments: approvalAction === 'request-docs'
              ? selectedDocRequests
                  .filter((index) => Boolean(docRequestComments[index]?.trim()))
                  .map((index) => ({
                    requirement: complianceData.checklist[index].requirement,
                    comment: docRequestComments[index],
                  }))
              : undefined,
          },
        },
      });
      await syncMutationRecord(response.data?.advanceForexCompliance, 'Workflow update failed');
      setShowApprovalForm(false);
      setApprovalReason('');
      setRemittanceAmount('');
      setRemittancePercent('');
      setSelectedDocRequests([]);
      setDocRequestComments({});
      setGlobalRequestNote('');
    } catch (error) {
      showTransientToast(`❌ ${(error as Error).message}`);
    }
  };

  const femaDetailsComplete = Boolean(
    complianceData.bankMetadata.femaReference?.trim() &&
    complianceData.bankMetadata.declarationDate?.trim()
  );

  const missingItems = complianceData.checklist.filter((item) => {
    if (!(item.required && item.status === 'pending')) {
      return false;
    }

    const isFemaChecklistItem = item.requirement.trim().toLowerCase() === 'fema declaration';
    if (isFemaChecklistItem && femaDetailsComplete) {
      return false;
    }

    return true;
  });

  return (
    <Page>
      <PageHeader>
        <HeaderContent>
          <Title>Forex Compliance — {complianceData.orderId}</Title>
          <Subtitle>Required for all foreign currency payments</Subtitle>
        </HeaderContent>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
          {onBack && (
            <Button onClick={onBack} variant="outline">
              ← Back to Orders
            </Button>
          )}
          <StatusBadge 
            variant={getStatusVariant(complianceData.complianceStatus)}
            label={getStatusLabel(complianceData.complianceStatus)}
          />
        </div>
      </PageHeader>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
        {/* Order Summary */}
        <CardContainer variant="elevated" padding="md">
          <SectionTitle>Order Summary</SectionTitle>
          <MetaGrid>
            <MetaItem>
              <MetaLabel>Order ID</MetaLabel>
              <MetaValue>{complianceData.orderId}</MetaValue>
            </MetaItem>
            <MetaItem>
              <MetaLabel>Buyer (Indian Entity)</MetaLabel>
              <MetaValue>{complianceData.buyer}</MetaValue>
            </MetaItem>
            <MetaItem>
              <MetaLabel>Supplier (Foreign Entity)</MetaLabel>
              <MetaValue>{complianceData.supplier}</MetaValue>
            </MetaItem>
            <MetaItem>
              <MetaLabel>Country of Supplier</MetaLabel>
              <MetaValue>{complianceData.country}</MetaValue>
            </MetaItem>
            <MetaItem>
              <MetaLabel>Currency</MetaLabel>
              <MetaValue>{complianceData.currency}</MetaValue>
            </MetaItem>
            <MetaItem>
              <MetaLabel>Total Order Value</MetaLabel>
              <MetaValue>{complianceData.currency} {complianceData.totalValue.toLocaleString('en-IN')}</MetaValue>
            </MetaItem>
            <MetaItem>
              <MetaLabel>Incoterms</MetaLabel>
              <MetaValue>{complianceData.incoterms}</MetaValue>
            </MetaItem>
            <MetaItem>
              <MetaLabel>Expected Shipment</MetaLabel>
              <MetaValue>{formatDisplayDate(complianceData.expectedShipmentDate)}</MetaValue>
            </MetaItem>
          </MetaGrid>
        </CardContainer>

        {/* Compliance Checklist */}
        <CardContainer variant="elevated" padding="md">
          <SectionTitle>Compliance Checklist</SectionTitle>

          {isFinance && !complianceData.isLocked && (!complianceData.bankMetadata.femaReference || !complianceData.bankMetadata.declarationDate) && (
            <AlertBox $type="error">
              FEMA Declaration is incomplete. Fill in both FEMA Reference and Declaration Date before approving compliance.
            </AlertBox>
          )}
          
          {missingItems.length > 0 && !['compliance-approved', 'remittance-initiated', 'remittance-completed', 'locked'].includes(complianceData.complianceStatus) && (
            <AlertBox $type="warning">
              ⚠️ Missing {missingItems.length} required item(s): {missingItems.map(i => i.requirement).join(', ')}
            </AlertBox>
          )}

          {complianceData.isLocked && (
            <AlertBox $type="info">
              🔒 Compliance is locked. No further edits allowed (post-shipment).
            </AlertBox>
          )}

          <ChecklistTable>
            <thead>
              <tr>
                <th>Requirement</th>
                <th>Owner</th>
                <th>Status</th>
                <th>File</th>
                <th>Notes</th>
              </tr>
            </thead>
            <tbody>
              {complianceData.checklist.map((item, index) => (
                <tr key={index} style={{ 
                  background: item.status === 'approved' ? '#f0fdf4' : 
                             item.status === 'rejected' ? '#fef2f2' : 
                             item.status === 'uploaded' ? '#fffbeb' : 'transparent' 
                }}>
                  <td data-label="Requirement">
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span>{item.requirement} {item.required && <span style={{ color: '#ef4444' }}>*</span>}</span>
                      {item.requestedOn && item.status === 'pending' && (
                        <span style={{ 
                          fontSize: 10, 
                          padding: '2px 6px', 
                          borderRadius: 4, 
                          background: '#fef3c7',
                          color: '#92400e',
                          fontWeight: 600 
                        }}>
                          REQUESTED
                        </span>
                      )}
                    </div>
                    {item.expiryDate && (
                      <div style={{ fontSize: 11, color: '#6b7280', marginTop: 2 }}>
                        Expires: {new Date(item.expiryDate).toLocaleDateString('en-IN')}
                      </div>
                    )}
                    {item.requestedOn && (
                      <div style={{ fontSize: 11, color: '#92400e', marginTop: 2 }}>
                        Requested: {new Date(item.requestedOn).toLocaleDateString('en-IN')}
                      </div>
                    )}
                  </td>
                  <td data-label="Owner">
                    <span style={{ 
                      fontSize: 11, 
                      padding: '2px 6px', 
                      borderRadius: 4, 
                      background: '#e5e7eb',
                      fontWeight: 600 
                    }}>
                      {getOwnerLabel(item.owner)}
                    </span>
                  </td>
                  <td data-label="Status">
                    {isAutoApprovedItem(item) ? (
                      <span style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 4,
                        background: '#dcfce7',
                        color: '#15803d',
                        fontWeight: 600,
                        fontSize: 12,
                        padding: '3px 8px',
                        borderRadius: 12,
                        border: '1px solid #86efac',
                      }}>
                        🟢 Auto Approved
                      </span>
                    ) : (
                      <>{getChecklistStatusIcon(item.status)} {item.status}</>
                    )}
                    {item.uploadedOn && (
                      <div style={{ fontSize: 11, color: '#6b7280' }}>
                        Uploaded: {new Date(item.uploadedOn).toLocaleDateString('en-IN')}
                      </div>
                    )}
                    {item.approvedOn && (
                      <div style={{ fontSize: 11, color: '#059669' }}>
                        Approved: {new Date(item.approvedOn).toLocaleDateString('en-IN')}
                      </div>
                    )}
                  </td>
                  <td data-label="File">
                    {canUploadChecklistItem(role, item.owner, item.status, complianceData.isLocked) ? (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                        <Input
                          type="file"
                          accept={getAllowedFileExtensions(item.requirement).map(ext => `.${ext}`).join(',')}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                            const file = e.target.files?.[0];
                            if (file) {
                              const ext = file.name.split('.').pop()?.toLowerCase() || '';
                              const allowed = getAllowedFileExtensions(item.requirement);
                              if (!allowed.includes(ext)) {
                                showTransientToast(`❌ Invalid file format for ${item.requirement}. Allowed: ${allowed.join(', ')}`);
                                e.currentTarget.value = '';
                                return;
                              }
                              if (uploadNote.trim()) {
                                handleFileUpload(index, file, uploadNote);
                              } else {
                                handleFileUpload(index, file);
                              }
                            }
                          }}
                          disabled={uploadingFile === index}
                          style={{ fontSize: 12 }}
                        />
                        <Input
                          placeholder="Optional note..."
                          value={uploadingFile === index ? '' : uploadNote}
                          onChange={(e) => setUploadNote(e.target.value)}
                          disabled={uploadingFile === index}
                          style={{ fontSize: 12 }}
                        />
                        {uploadingFile === index && <span style={{ fontSize: 11, color: '#6b7280' }}>Uploading...</span>}
                      </div>
                    ) : canReviewChecklistItem(role, item.status, complianceData.isLocked, item.requirement) ? (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                        {resolveDocumentUrl(item.file) ? (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                            <a
                              href={resolveDocumentUrl(item.file) || undefined}
                              target={isPdfFile(resolveDocumentUrl(item.file)) ? undefined : '_blank'}
                              rel={isPdfFile(resolveDocumentUrl(item.file)) ? undefined : 'noreferrer'}
                              onClick={(e) => {
                                const resolved = resolveDocumentUrl(item.file);
                                if (resolved && isPdfFile(resolved)) {
                                  e.preventDefault();
                                  openPdfPreview(resolved, item.requirement);
                                }
                              }}
                              style={{ color: '#3b82f6', textDecoration: 'none', fontSize: 13, cursor: 'pointer' }}
                            >
                              {getDocumentDisplayName(item.file, item.requirement)}
                            </a>
                          </div>
                        ) : (
                          <span style={{ color: '#6b7280', fontSize: 13 }}>{item.file}</span>
                        )}
                        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                          <Button 
                            onClick={() => handleItemAction(index, 'approve')}
                            variant="default"
                            style={{ fontSize: 11, padding: '4px 8px' }}
                          >
                            ✓ Approve
                          </Button>
                          <Button 
                            onClick={() => { setItemActionIndex(index); setItemActionType('reject'); }}
                            variant="outline"
                            style={{ fontSize: 11, padding: '4px 8px' }}
                          >
                            ✗ Reject
                          </Button>
                        </div>
                        {validationResults[index] && (
                          <div style={{
                            marginTop: 4,
                            padding: 8,
                            background: validationResults[index]!.is_valid_format ? '#f0fdf4' : '#fef2f2',
                            border: `1px solid ${validationResults[index]!.is_valid_format ? '#86efac' : '#fca5a5'}`,
                            borderRadius: 6,
                            fontSize: 11,
                          }}>
                            <div style={{ fontWeight: 600, marginBottom: 4, color: validationResults[index]!.is_valid_format ? '#059669' : '#dc2626' }}>
                              {validationResults[index]!.is_valid_format ? '✓ Format Valid' : '✗ Format Issues'} — Score: {validationResults[index]!.validation_score.toFixed(0)}%
                            </div>
                            {validationResults[index]!.checks.map((c, ci) => (
                              <div key={ci} style={{ color: c.passed ? '#15803d' : '#b91c1c', marginBottom: 2 }}>
                                {c.passed ? '✓' : '✗'} {c.details}
                              </div>
                            ))}
                            {validationResults[index]!.missing_required_fields.length > 0 && (
                              <div style={{ marginTop: 4, color: '#b91c1c' }}>
                                Missing: {validationResults[index]!.missing_required_fields.join(', ')}
                              </div>
                            )}
                            {validationResults[index]!.warnings.length > 0 && (
                              <div style={{ marginTop: 4, color: '#92400e' }}>
                                Warnings: {validationResults[index]!.warnings.join(' | ')}
                              </div>
                            )}
                            {validationResults[index]!.finalized_hs_code && (
                              <div style={{ marginTop: 4, color: '#1d4ed8', fontWeight: 700 }}>
                                Finalized HS Code: {String(validationResults[index]!.finalized_hs_code)}
                              </div>
                            )}
                            {!validationResults[index]!.finalized_hs_code && Array.isArray(validationResults[index]!.hs_code_candidates) && validationResults[index]!.hs_code_candidates!.length > 0 && (
                              <div style={{ marginTop: 4, color: '#92400e' }}>
                                Could not finalize a single HS code. Candidates: {validationResults[index]!.hs_code_candidates!.slice(0, 5).join(', ')}
                              </div>
                            )}
                            {validationResults[index]!.needs_manual_review && (
                              <div style={{ marginTop: 4, color: '#7c2d12' }}>
                                Manual review required{validationResults[index]!.manual_review_reasons?.length ? `: ${validationResults[index]!.manual_review_reasons.join(' | ')}` : ''}
                              </div>
                            )}
                            {Boolean(validationResults[index]!.extracted_data?.declaration_number) && (
                              <div style={{ marginTop: 4, color: '#374151' }}>
                                Declaration #: <strong>{String(validationResults[index]!.extracted_data.declaration_number)}</strong>
                              </div>
                            )}
                            {Boolean(validationResults[index]!.extracted_data?.hs_code_count) && (
                              <div style={{ color: '#374151' }}>
                                HS Codes: <strong>{String(validationResults[index]!.extracted_data.hs_code_count)}</strong>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    ) : item.file ? (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                        {resolveDocumentUrl(item.file) ? (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                            <a
                              href={resolveDocumentUrl(item.file) || undefined}
                              target={isPdfFile(resolveDocumentUrl(item.file)) ? undefined : '_blank'}
                              rel={isPdfFile(resolveDocumentUrl(item.file)) ? undefined : 'noreferrer'}
                              onClick={(e) => {
                                const resolved = resolveDocumentUrl(item.file);
                                if (resolved && isPdfFile(resolved)) {
                                  e.preventDefault();
                                  openPdfPreview(resolved, item.requirement);
                                }
                              }}
                              style={{ color: '#3b82f6', textDecoration: 'none', cursor: 'pointer' }}
                            >
                              {getDocumentDisplayName(item.file, item.requirement)}
                            </a>
                          </div>
                        ) : (
                          <span style={{ color: '#6b7280' }}>{item.file}</span>
                        )}
                        {validationResults[index] && (
                          <div style={{
                            padding: 8,
                            background: validationResults[index]!.is_valid_format ? '#f0fdf4' : '#fef2f2',
                            border: `1px solid ${validationResults[index]!.is_valid_format ? '#86efac' : '#fca5a5'}`,
                            borderRadius: 6,
                            fontSize: 11,
                            maxWidth: 320,
                          }}>
                            <div style={{ fontWeight: 600, marginBottom: 4, color: validationResults[index]!.is_valid_format ? '#059669' : '#dc2626' }}>
                              {validationResults[index]!.is_valid_format ? '✓ Format Valid' : '✗ Format Issues'} — Score: {validationResults[index]!.validation_score.toFixed(0)}%
                            </div>
                            {validationResults[index]!.checks.map((c, ci) => (
                              <div key={ci} style={{ color: c.passed ? '#15803d' : '#b91c1c', marginBottom: 2 }}>
                                {c.passed ? '✓' : '✗'} {c.details}
                              </div>
                            ))}
                            {Boolean(validationResults[index]!.extracted_data?.invoice_number) && (
                              <div style={{ marginTop: 4, color: '#374151' }}>
                                Invoice #: <strong>{String(validationResults[index]!.extracted_data.invoice_number)}</strong>
                              </div>
                            )}
                            {Boolean(validationResults[index]!.extracted_data?.total_amount) && (
                              <div style={{ color: '#374151' }}>
                                Amount: <strong>{String(validationResults[index]!.extracted_data.currency)} {Number(validationResults[index]!.extracted_data.total_amount).toLocaleString()}</strong>
                              </div>
                            )}
                            {validationResults[index]!.finalized_hs_code && (
                              <div style={{ color: '#1d4ed8', fontWeight: 700 }}>
                                Finalized HS Code: {String(validationResults[index]!.finalized_hs_code)}
                              </div>
                            )}
                            {validationResults[index]!.needs_manual_review && (
                              <div style={{ marginTop: 4, color: '#7c2d12' }}>
                                Manual review required{validationResults[index]!.manual_review_reasons?.length ? `: ${validationResults[index]!.manual_review_reasons.join(' | ')}` : ''}
                              </div>
                            )}
                            {Boolean(validationResults[index]!.extracted_data?.declaration_number) && (
                              <div style={{ color: '#374151' }}>
                                Declaration #: <strong>{String(validationResults[index]!.extracted_data.declaration_number)}</strong>
                              </div>
                            )}
                            {Boolean(validationResults[index]!.extracted_data?.hs_code_count) && (
                              <div style={{ color: '#374151' }}>
                                HS Codes: <strong>{String(validationResults[index]!.extracted_data.hs_code_count)}</strong>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    ) : '—'}
                  </td>
                  <td data-label="Notes">{item.notes || '—'}</td>
                </tr>
              ))}
            </tbody>
          </ChecklistTable>

          {pdfPreviewUrl && (
            <DocumentPreview
              title={`${pdfPreviewLabel} Preview`}
              src={pdfPreviewUrl}
              onClose={() => setPdfPreviewUrl(null)}
            />
          )}

          {/* Reject Item Modal */}
          {itemActionIndex !== null && itemActionType === 'reject' && (
            <div style={{ marginTop: 16, padding: 16, background: '#fef2f2', border: '1px solid #ef4444', borderRadius: 8 }}>
              <FormGroup>
                <Label>Rejection Reason (Required)</Label>
                <Input
                  value={itemActionReason}
                  onChange={(e) => setItemActionReason(e.target.value)}
                  placeholder="Specify why this document is being rejected..."
                  style={{ minHeight: 60 }}
                />
              </FormGroup>
              <FormActions>
                <Button 
                  onClick={() => {
                    if (itemActionReason.trim()) {
                      handleItemAction(itemActionIndex, 'reject', itemActionReason);
                    } else {
                      alert('Rejection reason is required');
                    }
                  }}
                  variant="default"
                >
                  Confirm Rejection
                </Button>
                <Button 
                  onClick={() => {
                    setItemActionIndex(null);
                    setItemActionType(null);
                    setItemActionReason('');
                  }}
                  variant="outline"
                >
                  Cancel
                </Button>
              </FormActions>
            </div>
          )}
        </CardContainer>

        {/* Remittance Plan */}
        <CardContainer variant="elevated" padding="md">
          <SectionTitle>Remittance Plan</SectionTitle>
          <div style={{ marginBottom: 16 }}>
            <MetaLabel>Total Remittance Amount</MetaLabel>
            <MetaValue style={{ fontSize: '18px', marginTop: 4 }}>
              {complianceData.currency} {complianceData.remittancePlan.totalAmount.toLocaleString('en-IN')}
            </MetaValue>
          </div>
          <ChecklistTable>
            <thead>
              <tr>
                <th>Tranche</th>
                <th>Percentage</th>
                <th>Amount</th>
                <th>Planned Date</th>
                <th>Milestone</th>
              </tr>
            </thead>
            <tbody>
              {complianceData.remittancePlan.tranches.map((tranche, index) => (
                <tr key={index}>
                  <td data-label="Tranche">{index + 1}</td>
                  <td data-label="Percentage">{tranche.percentage}%</td>
                  <td data-label="Amount">{complianceData.currency} {tranche.amount.toLocaleString('en-IN')}</td>
                  <td data-label="Planned Date">{formatDisplayDate(tranche.date)}</td>
                  <td data-label="Milestone">{tranche.milestone}</td>
                </tr>
              ))}
            </tbody>
          </ChecklistTable>
        </CardContainer>

        {/* Bank & RBI Metadata */}
        {isFinance && (
          <CardContainer variant="elevated" padding="md">
            <SectionTitle>Bank & RBI Metadata</SectionTitle>
            <MetaGrid>
              <MetaItem>
                <MetaLabel>AD Bank Name</MetaLabel>
                {!complianceData.isLocked ? (
                  <Input
                    value={complianceData.bankMetadata.adBankName || ''}
                    onChange={(e) => setComplianceData(prev => ({
                      ...prev,
                      bankMetadata: { ...prev.bankMetadata, adBankName: e.target.value },
                    }))}
                    placeholder="e.g., HDFC Bank"
                  />
                ) : (
                  <MetaValue>{complianceData.bankMetadata.adBankName || '—'}</MetaValue>
                )}
              </MetaItem>
              <MetaItem>
                <MetaLabel>AD Bank Code</MetaLabel>
                {!complianceData.isLocked ? (
                  <Input
                    value={complianceData.bankMetadata.adBankCode || ''}
                    onChange={(e) => setComplianceData(prev => ({
                      ...prev,
                      bankMetadata: { ...prev.bankMetadata, adBankCode: e.target.value },
                    }))}
                    placeholder="e.g., HDFC0001234"
                  />
                ) : (
                  <MetaValue>{complianceData.bankMetadata.adBankCode || '—'}</MetaValue>
                )}
              </MetaItem>
              <MetaItem>
                <MetaLabel>RBI Approval Number</MetaLabel>
                {!complianceData.isLocked ? (
                  <Input
                    value={complianceData.bankMetadata.rbiApprovalNumber || ''}
                    onChange={(e) => setComplianceData(prev => ({
                      ...prev,
                      bankMetadata: { ...prev.bankMetadata, rbiApprovalNumber: e.target.value },
                    }))}
                    placeholder="Optional RBI approval number"
                  />
                ) : (
                  <MetaValue>{complianceData.bankMetadata.rbiApprovalNumber || '—'}</MetaValue>
                )}
              </MetaItem>
              <MetaItem>
                <MetaLabel>FEMA Reference #</MetaLabel>
                {!complianceData.isLocked ? (
                  <>
                    <input
                      type="text"
                      value={complianceData.bankMetadata.femaReference || ''}
                      onChange={(e) => setComplianceData(prev => ({
                        ...prev,
                        bankMetadata: { ...prev.bankMetadata, femaReference: e.target.value },
                      }))}
                      placeholder="e.g., RBI/2024/12345"
                      style={{ width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px' }}
                    />
                    <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>
                      {complianceData.bankMetadata.femaReference ? '✓ Filled' : '⚠ Required'}
                    </div>
                  </>
                ) : (
                  <MetaValue>{complianceData.bankMetadata.femaReference || '—'}</MetaValue>
                )}
              </MetaItem>
                <MetaItem>
                  <MetaLabel>Declaration Date</MetaLabel>
                {!complianceData.isLocked ? (
                  <>
                    <input
                      type="date"
                      value={complianceData.bankMetadata.declarationDate || ''}
                      onChange={(e) => setComplianceData(prev => ({
                        ...prev,
                        bankMetadata: { ...prev.bankMetadata, declarationDate: e.target.value },
                      }))}
                      style={{ width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px' }}
                    />
                    <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>
                      {complianceData.bankMetadata.declarationDate ? '✓ Filled' : '⚠ Required'}
                    </div>
                  </>
                ) : (
                  <MetaValue>{complianceData.bankMetadata.declarationDate ? formatDisplayDate(complianceData.bankMetadata.declarationDate) : '—'}</MetaValue>
                )}
                </MetaItem>
              {complianceData.bankMetadata.bankSubmissionRef && (
                <MetaItem>
                  <MetaLabel>Bank Submission Ref</MetaLabel>
                  <MetaValue style={{ color: '#059669', fontWeight: 600 }}>
                    {complianceData.bankMetadata.bankSubmissionRef}
                  </MetaValue>
                </MetaItem>
              )}
            </MetaGrid>
            {!complianceData.isLocked && (
              <MetadataSyncStatus
                $tone={
                  bankMetadataSyncStatus === 'error'
                    ? 'error'
                    : bankMetadataSyncStatus === 'saved'
                      ? 'success'
                      : 'neutral'
                }
              >
                {bankMetadataSyncStatus === 'saving' && 'Saving bank metadata and checking whether FEMA can be auto-generated...'}
                {bankMetadataSyncStatus === 'saved' && 'Bank metadata saved. FEMA declaration is generated automatically once the bank and declaration details are complete.'}
                {bankMetadataSyncStatus === 'error' && 'Bank metadata could not be saved. Fix the values above and retry.'}
                {bankMetadataSyncStatus === 'idle' && 'This section auto-saves and generates the FEMA declaration without manual upload once the required values are filled.'}
              </MetadataSyncStatus>
            )}
          </CardContainer>
        )}

        {/* Document Upload for Suppliers */}
        {role === 'supplier' && complianceData.complianceStatus === 'docs-requested' && !complianceData.isLocked && (
          <CardContainer variant="elevated" padding="md">
            <SectionTitle>Document Upload Required</SectionTitle>
            <AlertBox $type="warning">
              📄 Finance has requested additional documents. Please upload the required files in the checklist above.
            </AlertBox>
            <FormActions>
              <Button 
                onClick={async () => {
                  try {
                    const response = await advanceForexCompliance({
                      variables: {
                        input: {
                          recordId: complianceData.orderId,
                          action: 'resubmit',
                          reason: 'Documents uploaded as requested',
                          actor: 'Supplier',
                        },
                      },
                    });
                    await syncMutationRecord(response.data?.advanceForexCompliance, 'Resubmission failed');
                  } catch (error) {
                    showTransientToast(`❌ ${(error as Error).message}`);
                  }
                }}
                variant="default"
                disabled={complianceData.checklist.filter(i => i.owner === 'supplier' && i.required).some(i => i.status === 'pending')}
              >
                Mark as Resubmitted
              </Button>
            </FormActions>
          </CardContainer>
        )}

        {/* Approval Controls */}
        {isFinance && !complianceData.isLocked && (
          <CardContainer variant="elevated" padding="md">
            <SectionTitle>Approval Controls</SectionTitle>
            
            {!showApprovalForm ? (
              <FormActions>
                {complianceData.complianceStatus === 'draft' && (
                  <Button 
                    onClick={() => { setShowApprovalForm(true); setApprovalAction('submit'); }}
                    variant="default"
                  >
                    Submit for Review
                  </Button>
                )}
                
                {['submitted', 'docs-resubmitted', 'docs-requested'].includes(complianceData.complianceStatus) && (
                  <>
                    <Button 
                      onClick={() => { setShowApprovalForm(true); setApprovalAction('approve'); }}
                      variant="default"
                      disabled={missingItems.length > 0 || !complianceData.bankMetadata.femaReference || !complianceData.bankMetadata.declarationDate}
                      title={
                        missingItems.length > 0
                          ? 'Cannot approve with missing required items'
                          : !complianceData.bankMetadata.femaReference
                          ? 'FEMA Reference is required'
                          : !complianceData.bankMetadata.declarationDate
                          ? 'Declaration Date is required'
                          : 'Approve compliance'
                      }
                    >
                      Approve Compliance
                    </Button>
                    <Button 
                      onClick={() => { setShowApprovalForm(true); setApprovalAction('request-docs'); }}
                      variant="outline"
                    >
                      {complianceData.complianceStatus === 'docs-requested' ? 'Send Reminder / Request More Docs' : 'Request Missing Docs'}
                    </Button>
                  </>
                )}

                {complianceData.complianceStatus === 'compliance-approved' && (
                  <Button 
                    onClick={() => {
                      setShowApprovalForm(true);
                      setApprovalAction('approve');
                      const firstTranche = complianceData.remittancePlan.tranches?.[0];
                      setRemittanceAmount(firstTranche?.amount ? String(firstTranche.amount) : '');
                      setRemittancePercent(firstTranche?.percentage ? String(firstTranche.percentage) : '');
                    }}
                    variant="default"
                  >
                    Initiate Remittance
                  </Button>
                )}

                {complianceData.complianceStatus === 'remittance-initiated' && (
                  <Button
                    onClick={() => { setShowApprovalForm(true); setApprovalAction('complete-remittance'); }}
                    variant="default"
                  >
                    Complete Remittance
                  </Button>
                )}
              </FormActions>
            ) : (
              <>
                {approvalAction === 'request-docs' ? (
                  <>
                    <FormGroup>
                      <Label>Select Document(s) to Request (Required)</Label>
                      <div style={{ border: '1px solid #d1d5db', borderRadius: 6, padding: 12, maxHeight: 200, overflowY: 'auto' }}>
                        {complianceData.checklist.map((item, idx) => (
                          item.status !== 'approved' && (
                            <div key={idx} style={{ marginBottom: 8 }}>
                              <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                                <StyledCheckbox
                                  checked={selectedDocRequests.includes(idx)}
                                  onChange={(e) => {
                                    if (e.target.checked) {
                                      setSelectedDocRequests([...selectedDocRequests, idx]);
                                    } else {
                                      setSelectedDocRequests(selectedDocRequests.filter(i => i !== idx));
                                      const newComments = { ...docRequestComments };
                                      delete newComments[idx];
                                      setDocRequestComments(newComments);
                                    }
                                  }}
                                />
                                <span style={{ fontSize: 14 }}>
                                  {item.requirement}
                                  {item.required && <span style={{ color: '#ef4444' }}> *</span>}
                                  <span style={{ fontSize: 11, color: '#6b7280', marginLeft: 8 }}>({getOwnerLabel(item.owner)})</span>
                                </span>
                              </label>
                            </div>
                          )
                        ))}
                      </div>
                    </FormGroup>

                    {selectedDocRequests.length > 0 && (
                      <div style={{ marginTop: 16, padding: 12, background: '#f9fafb', borderRadius: 6 }}>
                        <Label style={{ marginBottom: 12 }}>Per-Document Comments (Recommended)</Label>
                        {selectedDocRequests.map(idx => (
                          <FormGroup key={idx} style={{ marginBottom: 12 }}>
                            <Label style={{ fontSize: 13, fontWeight: 500 }}>
                              {complianceData.checklist[idx].requirement}
                            </Label>
                            <Input
                              value={docRequestComments[idx] || ''}
                              onChange={(e) => setDocRequestComments({ ...docRequestComments, [idx]: e.target.value })}
                              placeholder="e.g., Please confirm 8-digit HS code as per invoice line items"
                              style={{ fontSize: 13 }}
                            />
                          </FormGroup>
                        ))}
                      </div>
                    )}

                    <FormGroup style={{ marginTop: 16 }}>
                      <Label>Overall Note (Optional)</Label>
                      <Input
                        value={globalRequestNote}
                        onChange={(e) => setGlobalRequestNote(e.target.value)}
                        placeholder="e.g., Documents needed for HDFC AD bank remittance approval"
                        style={{ minHeight: 60 }}
                      />
                    </FormGroup>

                    <FormActions>
                      <Button onClick={handleApproval} variant="default" disabled={selectedDocRequests.length === 0}>
                        Confirm Request ({selectedDocRequests.length} document{selectedDocRequests.length !== 1 ? 's' : ''})
                      </Button>
                      <Button onClick={() => {
                        setShowApprovalForm(false);
                        setSelectedDocRequests([]);
                        setDocRequestComments({});
                        setGlobalRequestNote('');
                      }} variant="outline">Cancel</Button>
                    </FormActions>
                  </>
                ) : (
                  <>
                    {approvalAction === 'approve' && complianceData.complianceStatus === 'compliance-approved' && (
                      <>
                        <AlertBox $type="info">
                          Bank metadata is sourced from supplier documents. You only need to provide minimum remittance values.
                        </AlertBox>
                        <MetaGrid style={{ marginBottom: 12 }}>
                          <MetaItem>
                            <MetaLabel>AD Bank Name</MetaLabel>
                            <MetaValue>{complianceData.bankMetadata.adBankName || '—'}</MetaValue>
                          </MetaItem>
                          <MetaItem>
                            <MetaLabel>AD Bank Code</MetaLabel>
                            <MetaValue>{complianceData.bankMetadata.adBankCode || '—'}</MetaValue>
                          </MetaItem>
                        </MetaGrid>
                        <MetaGrid>
                          <FormGroup>
                            <Label>Remittance Amount ({complianceData.currency})</Label>
                            <Input
                              type="number"
                              value={remittanceAmount}
                              onChange={(e) => setRemittanceAmount(e.target.value)}
                              placeholder="Enter amount"
                            />
                          </FormGroup>
                          <FormGroup>
                            <Label>Remittance Percent (%)</Label>
                            <Input
                              type="number"
                              value={remittancePercent}
                              onChange={(e) => setRemittancePercent(e.target.value)}
                              placeholder="Enter percent"
                            />
                          </FormGroup>
                        </MetaGrid>
                      </>
                    )}
                    <FormGroup>
                      <Label>
                        {approvalAction === 'submit' && 'Submission Notes'}
                        {approvalAction === 'approve' && complianceData.complianceStatus === 'compliance-approved' && 'Remittance Initiation Details'}
                        {approvalAction === 'approve' && complianceData.complianceStatus !== 'compliance-approved' && 'Approval Comments'}
                        {approvalAction === 'complete-remittance' && 'Remittance Completion Details'}
                      </Label>
                      <Input
                        value={approvalReason}
                        onChange={(e) => setApprovalReason(e.target.value)}
                        placeholder={
                          approvalAction === 'approve' && complianceData.complianceStatus === 'compliance-approved'
                            ? 'Enter bank reference / UTR, tranche amount, value date, and confirmation notes...'
                            : approvalAction === 'complete-remittance'
                              ? 'Enter remittance completion reference, settlement date, and closure notes...'
                              : 'Enter details...'
                        }
                        style={{ minHeight: 80 }}
                      />
                    </FormGroup>
                    <FormActions>
                      <Button onClick={handleApproval} variant="default">
                        Confirm {approvalAction === 'submit' ? 'Submission' : 
                                 approvalAction === 'approve' && complianceData.complianceStatus === 'compliance-approved' ? 'Initiation' : 
                                 approvalAction === 'complete-remittance' ? 'Completion' :
                                 'Approval'}
                      </Button>
                      <Button onClick={() => setShowApprovalForm(false)} variant="outline">Cancel</Button>
                    </FormActions>
                  </>
                )}
              </>
            )}
          </CardContainer>
        )}

        {/* Approval History */}
        <CardContainer variant="elevated" padding="md">
          <SectionTitle>Approval & Audit History</SectionTitle>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {complianceData.approvalHistory.map((entry, index) => (
              <div key={index} style={{ 
                padding: 12, 
                background: '#f9fafb', 
                borderRadius: 6,
                fontSize: 14,
              }}>
                <div style={{ fontWeight: 600, color: '#374151', marginBottom: 4 }}>
                  {entry.action}
                </div>
                <div style={{ fontSize: 12, color: '#6b7280' }}>
                  {new Date(entry.timestamp).toLocaleString('en-US')} • {entry.by}
                </div>
                {entry.reason && (
                  <div style={{ fontSize: 13, color: '#4b5563', marginTop: 4 }}>
                    Reason: {entry.reason}
                  </div>
                )}
              </div>
            ))}
          </div>
        </CardContainer>
      </div>

      {/* Toast Notification */}
      {showToast && (
        <div style={{
          position: 'fixed',
          bottom: 24,
          right: 24,
          background: '#10b981',
          color: 'white',
          padding: '12px 20px',
          borderRadius: 8,
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          zIndex: 9999,
          animation: 'slideIn 0.3s ease-out',
          maxWidth: 400,
        }}>
          <span style={{ fontSize: 20 }}>✓</span>
          <span style={{ fontSize: 14, fontWeight: 500 }}>{toastMessage}</span>
        </div>
      )}
    </Page>
  );
};
